/**
 * 답변 보존 캠페인 재배정 API — /api/admin/campaign-remap
 *
 * 한 업체의 배정 캠페인을 from→to 로 옮기되, 대상 질문지에 존재하는 문항의 답변은
 * 삭제하지 않고 이어붙이는 관리자 기능. 지금까지 담당자가 수동 SQL 로 처리하던 작업이다.
 *
 * ⛔ 이 API 는 **되돌릴 수 없는 삭제**를 포함한다(대상 질문지에 없는 문항의 답변,
 *    감사자 평가 전량). 그래서 서버가 2단계를 강제한다:
 *      1) preview  — DB 를 바꾸지 않고 영향 범위와 fingerprint 를 계산
 *      2) execute  — preview 가 준 fingerprint + 삭제 건수 + 영향 프로젝트 목록을
 *                    **그대로 되돌려 보내야** 실행된다. 하나라도 어긋나면 409/422.
 *    즉 화면은 preview 응답의 execute_requires 를 손대지 말고 그대로 전달해야 한다.
 *
 * 엔드포인트를 이 파일에 모은 이유: 호출부(탭 컴포넌트)가 2개로 나뉘는데, 위 계약을
 * 양쪽에서 각자 조립하면 한쪽만 어긋나도 실행이 막히거나(가벼움) 잘못된 확인값을
 * 보내게 된다(무거움). 계약을 한 곳에 고정한다.
 */
import createApiInstance from '@core/utils/api/custom.instance';
import { getErrorMessage } from '@core/utils/api/errorMessage';

const api = createApiInstance();

const BASE = '/admin/campaign-remap';

/**
 * 재배정 미리보기 (읽기 전용 — DB 무변경).
 * @returns {Promise<Object>} keep/delete 건수, 삭제될 sub_code 목록, 감사자 평가 삭제
 *   건수, to 선재 충돌, 영향 프로젝트, warnings, execute_requires
 */
export async function previewRemap({companyId, fromCampaignId, toCampaignId}) {
    const {data} = await api.post(`${BASE}/preview`, {
        company_id: companyId,
        from_campaign_id: fromCampaignId,
        to_campaign_id: toCampaignId,
    });
    return data;
}

/**
 * 재배정 실행.
 * @param {Object} executeRequires preview 응답의 execute_requires 를 **그대로** 전달한다.
 *   값을 화면에서 다시 계산하면 안 된다 — 서버가 자기 계산과 대조해 불일치 시 거절한다.
 */
export async function executeRemap({companyId, fromCampaignId, toCampaignId, executeRequires}) {
    const {data} = await api.post(`${BASE}/execute`, {
        company_id: companyId,
        from_campaign_id: fromCampaignId,
        to_campaign_id: toCampaignId,
        fingerprint: executeRequires.fingerprint,
        confirm_delete_answers: executeRequires.confirm_delete_answers,
        confirm_delete_evaluations: executeRequires.confirm_delete_evaluations,
        confirm_affected_projects: executeRequires.confirm_affected_projects,
    });
    return data;
}

/** 실행 이력 목록 (백업 본문 제외 요약). */
export async function fetchRemapLogs({companyId = null, limit = 50} = {}) {
    const params = {limit};
    if (companyId) params.company_id = companyId;
    const {data} = await api.get(`${BASE}/logs`, {params});
    return (data && data.logs) || [];
}

/**
 * 실행 이력 상세 — 삭제된 원본(before-image) 회수.
 * @param {boolean} includeBackups false 면 건수만. 삭제 답변이 많으면 응답이 크다
 *   (실측 42건 ≈ 300KB) — 목록/요약 용도에는 false 를 쓴다.
 */
export async function fetchRemapLogDetail(logId, {includeBackups = true} = {}) {
    const {data} = await api.get(`${BASE}/logs/${logId}`, {
        params: {backups: includeBackups ? 'true' : 'false'},
    });
    return data;
}

/** 백엔드 업무 예외를 사용자 문구로. 서버가 준 message 를 최우선으로 쓴다(성공 위장 금지). */
export function remapErrorMessage(error, fallback = '요청 처리 중 오류가 발생했습니다.') {
    return getErrorMessage(error, '') || (error && error.message) || fallback;
}
